<template>
  <div class="ccma-wallet-information">
    <h4>{{ t('consultantCorner.walletInformation.WalletInformation') }}</h4>
    <p class="wallet-description">{{ t('consultantCorner.walletInformation.WalletDescription') }}</p>

 


    <!-- Loading State -->
    <div v-if="loading" class="loading-state">
      <i class="fas fa-spinner fa-spin"></i>
      {{ t('consultantCorner.walletInformation.LoadingCards') }}
    </div>

    <!-- Error State -->
    <div v-if="error" class="error-state">
      <div class="alert alert-danger">
        <i class="fas fa-exclamation-circle"></i>
        {{ error }}
        <button class="btn btn-sm btn-outline-danger ms-2" @click="loadCards">
          {{ t('consultantCorner.walletInformation.TryAgain') }}
        </button>
      </div>
    </div>

    <!-- Cards List -->
    <div v-if="!loading && !error" class="cards-section">
      <div class="section-header">
        <button 
          class="btn btn-primary" 
          @click="showAddCard = true"
          :disabled="processing || addingCard"
        >
          <i v-if="!addingCard" class="fas fa-plus"></i>
          <i v-else class="fas fa-spinner fa-spin"></i>
          {{ addingCard ? t('consultantCorner.walletInformation.AddingCard') : t('consultantCorner.walletInformation.AddCard') }}
        </button>
      </div>

      <!-- Empty State -->
      <div v-if="cards.length === 0" class="empty-state">
        <div class="empty-content">
          <i class="fas fa-credit-card"></i>
          <h6>{{ t('consultantCorner.walletInformation.NoCardsFound') }}</h6>
          <p>{{ t('consultantCorner.walletInformation.NoCardsDescription') }}</p>
          <button 
            class="btn btn-primary" 
            @click="showAddCard = true"
            :disabled="processing || addingCard"
          >
            <i v-if="!addingCard" class="fas fa-plus"></i>
            <i v-else class="fas fa-spinner fa-spin"></i>
            {{ addingCard ? t('consultantCorner.walletInformation.AddingCard') : t('consultantCorner.walletInformation.AddCard') }}
          </button>
        </div>
      </div>

      <!-- Cards Grid -->
      <div v-else class="cards-grid">
        <div v-for="card in cards" :key="card.accountNumber" class="card-item">
          <div class="card-content">
            <div class="card-header-row">
              <h6 class="card-title">
                <i v-if="card.creditCardType?.toLowerCase() === 'paypal'" class="fab fa-paypal me-2 text-primary"></i>
                {{ card.accountName || (card.creditCardType?.toLowerCase() === 'paypal' ? t('consultantCorner.walletInformation.PayPalAccount') : '') }}
              </h6>
              <span v-if="card.creditCardType?.toLowerCase() !== 'paypal' && isExpired(card.expirationDate)" class="badge badge-danger">
                {{ t('consultantCorner.walletInformation.ExpiredCard') }}
              </span>
            </div>
            
            <div class="card-info">
              <!-- PayPal Specific Layout -->
              <template v-if="card.creditCardType?.toLowerCase() === 'paypal'">
                <div class="card-field">
                  <label class="field-label">{{ t('consultantCorner.walletInformation.PayPalEmail') }}</label>
                  <span class="field-value">{{ card.accountNumber }}</span>
                </div>
                <div class="card-field">
                  <label class="field-label">{{ t('consultantCorner.walletInformation.Use') }}</label>
                  <span class="field-value">{{ card.usage }}</span>
                </div>
              </template>
              
              <!-- Credit Card Standard Layout -->
              <template v-else>
                <div class="card-field">
                  <label class="field-label">{{ t('consultantCorner.walletInformation.CardNumber') }}</label>
                  <span class="field-value">{{ card.accountNumber }}</span>
                </div>
                <div class="card-field">
                  <label class="field-label">{{ t('consultantCorner.walletInformation.ExpirationDate') }}</label>
                  <span class="field-value">{{ formatDate(card.expirationDate) }}</span>
                </div>
                <div class="card-field">
                  <label class="field-label">{{ t('consultantCorner.walletInformation.HolderName') }}</label>
                  <span class="field-value">{{ card.accountHolderName }}</span>
                </div>
                <div class="card-field">
                  <label class="field-label">{{ t('consultantCorner.walletInformation.Use') }}</label>
                  <span class="field-value">{{ card.usage }}</span>
                </div>
              </template>
            </div>
          </div>

          <div class="card-actions">
            <button 
              class="btn btn-sm btn-outline-warning btn-primary-payment"
              @click="setAsPrimary(card)"
              :disabled="processing"
            >
              <i class="fas fa-star"></i>
              {{ t('consultantCorner.walletInformation.SetAsPrimary') }}
            </button>
            <div class="card-actions-row">
              <button 
                class="btn btn-sm btn-outline-primary"
                @click="editCard(card)"
                :disabled="processing"
              >
                <i class="fas fa-edit"></i>
                {{ t('consultantCorner.walletInformation.Name') }}
              </button>
              <button 
                v-if="card.creditCardType?.toLowerCase() !== 'paypal'"
                class="btn btn-sm btn-outline-success"
                @click="extendCard(card)"
                :disabled="processing"
              >
                <i class="fas fa-calendar-plus"></i>
                {{ t('consultantCorner.walletInformation.Extend') }}
              </button>
              <button 
                class="btn btn-sm btn-outline-danger"
                @click="deleteCard(card)"
                :disabled="processing"
              >
                <i class="fas fa-trash"></i>
                {{ t('consultantCorner.walletInformation.Delete') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Add Card Modal -->
    <div v-if="showAddCard" class="modal-overlay" @click="closeAddCard">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5>{{ t('consultantCorner.walletInformation.AddCard') }}</h5>
          <button class="btn-close" @click="closeAddCard">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="addingCard" class="loading-overlay">
            <div class="loading-content">
              <i class="fas fa-spinner fa-spin fa-2x"></i>
              <p>{{ t('consultantCorner.walletInformation.ProcessingCard') }}</p>
            </div>
          </div>
          <BraintreeWalletEntry 
            @wallet-added="walletAddedHandler" 
            @loading="handleBraintreeLoading"
          />
        </div>
      </div>
    </div>

    <!-- Edit Card Modal -->
    <div v-if="showEditCard && selectedCard" class="modal-overlay" @click="closeEditCard">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5>{{ t('consultantCorner.walletInformation.Name') }}</h5>
          <button class="btn-close" @click="closeEditCard">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="updateCard">
            <div class="form-group">
              <label>{{ t('consultantCorner.walletInformation.AccountName') }}</label>
              <input 
                v-model="editForm.accountName" 
                type="text" 
                class="form-control"
                :class="{ 'is-invalid': editErrors.accountName }"
                required
              >
              <div v-if="editErrors.accountName" class="invalid-feedback">
                {{ editErrors.accountName }}
              </div>
            </div>
            <div class="modal-actions">
              <button type="button" class="btn btn-secondary" @click="closeEditCard">
                {{ t('consultantCorner.walletInformation.Cancel') }}
              </button>
              <button type="submit" class="btn btn-primary" :disabled="processing">
                <i v-if="processing" class="fas fa-spinner fa-spin"></i>
                {{ processing ? t('consultantCorner.walletInformation.UpdatingCard') : t('consultantCorner.walletInformation.Save') }}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- Extend Card Modal -->
    <div v-if="showExtendCard && selectedCard" class="modal-overlay" @click="closeExtendCard">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5>{{ t('consultantCorner.walletInformation.ExtendCardTitle') }}</h5>
          <button class="btn-close" @click="closeExtendCard">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ t('consultantCorner.walletInformation.ExtendCardDescription') }}</p>
          <BraintreeWalletEntry 
            mode="update"
            :customerCardGuid="selectedCard.customerCardGuid"
            :cusWalletGuid="selectedCard.cusWalletGuid"
            :expirationDate="selectedCard.expirationDate ? new Date(selectedCard.expirationDate).toISOString().split('T')[0] : ''"
            @wallet-updated="walletUpdatedHandler" 
          />
        </div>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div v-if="showDeleteConfirm && selectedCard" class="modal-overlay" @click="closeDeleteConfirm">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5>{{ t('consultantCorner.walletInformation.ConfirmDeleteCard') }}</h5>
          <button class="btn-close" @click="closeDeleteConfirm">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <p>{{ t('consultantCorner.walletInformation.ConfirmDeleteCardMessage') }}</p>
          <div class="card-preview">
            <strong>{{ selectedCard.accountName }}</strong><br>
            {{ selectedCard.accountNumber }} - {{ selectedCard.usage }}
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="closeDeleteConfirm">
            {{ t('consultantCorner.walletInformation.Cancel') }}
          </button>
          <button class="btn btn-danger" @click="confirmDelete" :disabled="processing">
            <i v-if="processing" class="fas fa-spinner fa-spin"></i>
            {{ processing ? t('consultantCorner.walletInformation.DeletingCard') : t('consultantCorner.walletInformation.Confirm') }}
          </button>
        </div>
      </div>
    </div>

    <!-- Success/Error Notifications -->
    <div v-if="notification" class="notification" :class="notification.type">
      <i :class="notification.type === 'success' ? 'fas fa-check-circle' : 'fas fa-exclamation-circle'"></i>
      {{ notification.message }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { ref, onMounted, computed } from 'vue'
import BraintreeWalletEntry from '../PaymentGateways/Braintree/BraintreeWalletEntry.vue'
import { useLogger } from 'vue-logger-plugin'
import { 
  WalletGetList, 
  WalletGetListWithGuidSupport,
  WalletDelete, 
  WalletExtend,
  WalletUpdateName,
  WalletSetDefault
} from '@/api/Order'
import type { IWalletItem } from '@/types/OrderTypes'

const logger = useLogger()
const { t } = useI18n()

// State
const cards = ref<IWalletItem[]>([])
const loading = ref(false)
const processing = ref(false)
const addingCard = ref(false)
const error = ref('')
const notification = ref<{ type: 'success' | 'error', message: string } | null>(null)

// Modals
const showAddCard = ref(false)
const showEditCard = ref(false)
const showExtendCard = ref(false)
const showDeleteConfirm = ref(false)
const selectedCard = ref<IWalletItem | null>(null)

// Forms
const editForm = ref({
  accountName: ''
})
const extendForm = ref({
  expirationDate: ''
})
const editErrors = ref<Record<string, string>>({})
const extendErrors = ref<Record<string, string>>({})

// Computed - removed hasDefaultCard since new API doesn't have isDefault

// Date formatting and validation functions
function formatDate(date: Date): string {
  if (!date) return ''
  const d = new Date(date)
  if (isNaN(d.getTime()) || d.getFullYear() <= 1900) return ''
  return d.toLocaleDateString()
}

function isExpired(expirationDate: Date): boolean {
  if (!expirationDate) return false
  const expDate = new Date(expirationDate)
  if (isNaN(expDate.getTime()) || expDate.getFullYear() <= 1900) return false
  const now = new Date()
  return expDate < now
}

// Validation functions
function validateExpirationDate(date: string): boolean {
  if (!date) return false
  const expDate = new Date(date)
  const now = new Date()
  return expDate > now
}

// API Functions
async function loadCards() {
  loading.value = true
  error.value = ''
  
  try {
    // Use the new API with GUID support to avoid JavaScript rounding issues
    const response = await WalletGetListWithGuidSupport()
    if (response.responseStatus?.errorCode) {
      throw new Error(response.responseStatus.message || t('consultantCorner.walletInformation.LoadCardsError'))
    }
    cards.value = response.items || []
    
    // Log the GUIDs to verify they are correct strings
    logger.debug('Wallet cards loaded with proper GUIDs:', cards.value.map(card => ({
      accountNumber: card.accountNumber,
      customerCardGuid: card.customerCardGuid,
      cusWalletGuid: card.cusWalletGuid
    })))
  } catch (err) {
    logger.error('Error loading cards:', err)
    error.value = err instanceof Error ? err.message : t('consultantCorner.walletInformation.LoadCardsError')
  } finally {
    loading.value = false
  }
}

// Removed setDefaultCard function since new API doesn't support default cards

async function confirmDelete() {
  if (!selectedCard.value) return
  
  processing.value = true
  
  try {
    const response = await WalletDelete({ cusWalletGuid: selectedCard.value.cusWalletGuid })
    if (response.responseStatus?.errorCode) {
      throw new Error(response.responseStatus.message || t('consultantCorner.walletInformation.DeleteCardError'))
    }
    
    showNotification('success', t('consultantCorner.walletInformation.CardDeleted'))
    closeDeleteConfirm()
    
    // Reload cards to refresh the component
    await loadCards()
  } catch (err) {
    logger.error('Error deleting card:', err)
    showNotification('error', err instanceof Error ? err.message : t('consultantCorner.walletInformation.DeleteCardError'))
  } finally {
    processing.value = false
  }
}

async function updateCard() {
  if (!selectedCard.value) return
  
  editErrors.value = {}
  
  // Validate form
  if (!editForm.value.accountName.trim()) {
    editErrors.value.accountName = t('consultantCorner.walletInformation.AccountNameRequired')
    return
  }
  
  processing.value = true
  
  try {
    const response = await WalletUpdateName({
      cusWalletGuid: selectedCard.value.cusWalletGuid,
      cardName: editForm.value.accountName.trim()
    })
    
    if (response.responseStatus?.errorCode) {
      throw new Error(response.responseStatus.message || t('consultantCorner.walletInformation.UpdateCardError'))
    }
    
    showNotification('success', t('consultantCorner.walletInformation.CardUpdated'))
    closeEditCard()
    
    // Reload cards to refresh the component
    await loadCards()
  } catch (err) {
    logger.error('Error updating card:', err)
    showNotification('error', err instanceof Error ? err.message : t('consultantCorner.walletInformation.UpdateCardError'))
  } finally {
    processing.value = false
  }
}

async function updateCardExpiration() {
  if (!selectedCard.value) return
  
  extendErrors.value = {}
  
  // Validate expiration date
  if (!extendForm.value.expirationDate) {
    extendErrors.value.expirationDate = t('consultantCorner.walletInformation.ExpirationDateRequired')
    return
  }
  
  if (!validateExpirationDate(extendForm.value.expirationDate)) {
    extendErrors.value.expirationDate = t('consultantCorner.walletInformation.InvalidExpirationDate')
    return
  }
  
  processing.value = true
  
  try {
    const response = await WalletExtend({
      customerCardGuid: selectedCard.value.customerCardGuid,
      cusWalletGuid: selectedCard.value.cusWalletGuid,
      expirationDate: extendForm.value.expirationDate
    })
    
    if (response.responseStatus?.errorCode) {
      throw new Error(response.responseStatus.message || t('consultantCorner.walletInformation.ExtendCardError'))
    }
    
    // Update local state
    const cardIndex = cards.value.findIndex(c => c.accountNumber === selectedCard.value!.accountNumber)
    if (cardIndex !== -1) {
      cards.value[cardIndex].expirationDate = new Date(extendForm.value.expirationDate)
    }
    
    showNotification('success', t('consultantCorner.walletInformation.CardExtended'))
    closeExtendCard()
  } catch (err) {
    logger.error('Error extending card:', err)
    showNotification('error', err instanceof Error ? err.message : t('consultantCorner.walletInformation.ExtendCardError'))
  } finally {
    processing.value = false
  }
}

// Modal functions
function editCard(card: IWalletItem) {
  selectedCard.value = card
  editForm.value = {
    accountName: card.accountName
  }
  editErrors.value = {}
  showEditCard.value = true
}

function extendCard(card: IWalletItem) {
  selectedCard.value = card
  extendForm.value = {
    expirationDate: card.expirationDate ? new Date(card.expirationDate).toISOString().split('T')[0] : ''
  }
  extendErrors.value = {}
  showExtendCard.value = true
}

function deleteCard(card: IWalletItem) {
  selectedCard.value = card
  showDeleteConfirm.value = true
}

async function setAsPrimary(card: IWalletItem) {
  processing.value = true
  
  try {
    const response = await WalletSetDefault({
      cusWalletGuid: card.cusWalletGuid,
      isDefault: true
    })
    
    if (response.responseStatus?.errorCode) {
      throw new Error(response.responseStatus.message || t('consultantCorner.walletInformation.UpdateCardError'))
    }
    
    showNotification('success', t('consultantCorner.walletInformation.DefaultCardSet'))
    
    // Reload cards to refresh the component
    await loadCards()
  } catch (err) {
    logger.error('Error setting card as primary:', err)
    showNotification('error', err instanceof Error ? err.message : t('consultantCorner.walletInformation.UpdateCardError'))
  } finally {
    processing.value = false
  }
}

function closeAddCard() {
  showAddCard.value = false
}

function closeEditCard() {
  showEditCard.value = false
  selectedCard.value = null
  editForm.value = { accountName: '' }
  editErrors.value = {}
}

function closeExtendCard() {
  showExtendCard.value = false
  selectedCard.value = null
  extendForm.value = { expirationDate: '' }
  extendErrors.value = {}
}

function closeDeleteConfirm() {
  showDeleteConfirm.value = false
  selectedCard.value = null
}

function showNotification(type: 'success' | 'error', message: string) {
  notification.value = { type, message }
  setTimeout(() => {
    notification.value = null
  }, 5000)
}

function walletAddedHandler() {
  logger.debug('walletAddedHandler')
  addingCard.value = false
  showAddCard.value = false
  loadCards() // Reload cards after adding
  showNotification('success', t('consultantCorner.walletInformation.CardAdded'))
}

function handleBraintreeLoading(isLoading: boolean) {
  addingCard.value = isLoading
}

function walletUpdatedHandler() {
  logger.debug('walletUpdatedHandler')
  showExtendCard.value = false
  loadCards() // Reload cards after updating
  showNotification('success', t('consultantCorner.walletInformation.CardExtended'))
}

// Lifecycle
onMounted(() => {
  loadCards()
})
</script>

<style lang="scss" scoped>
.ccma-wallet-information {
  padding: $card-padding;
}

.wallet-description {
  color: #666;
  margin-bottom: 1.5rem;
}

.pci-compliance-notice {
  background-color: #e8f5e9;
  border: 1px solid #28a745;
  border-radius: $radius-md;
  padding: 1.5rem;
  margin-bottom: 2rem;
}

.notice-header {
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
}

.notice-header i {
  @include typography-h3;
  color: #28a745;
  margin-right: 0.5rem;
}

.notice-header h5 {
  margin: 0;
  color: #28a745;
}

.alert {
  padding: 1rem;
  border-radius: $radius-md;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.alert-warning {
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
  color: #856404;
}

.alert-danger {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

.loading-state, .error-state {
  text-align: center;
  padding: 2rem;
}

.loading-state i {
  @include typography-h2;
  color: #e67e22;
  margin-bottom: 1rem;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.section-header h5 {
  color: #e67e22;
  margin: 0;
}

.empty-state {
  text-align: center;
  padding: 3rem;
  background-color: var(--vt-c-white-soft);
  border-radius: $radius-md;
  border: 2px dashed #dee2e6;
}

.empty-content i {
  @include typography-display;
  color: #6c757d;
  margin-bottom: 1rem;
}

.empty-content h6 {
  color: #495057;
  margin-bottom: 0.5rem;
}

.empty-content p {
  color: #6c757d;
  margin-bottom: 1.5rem;
}

.cards-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 1rem;
}

.card-item {
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: $radius-lg;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(black, 0.1);
  transition: all 0.2s ease;
}

.card-item:hover {
  box-shadow: 0 2px 8px rgba(black, 0.1);
  border-color: #e67e22;
}

.card-content {
  padding: 1rem;
}

.card-header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid var(--vt-c-white-soft);
}

.card-title {
  @include typography-body;
  @include font-weight-semibold;
  margin: 0;
  color: #333;
}

.card-info {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.card-field {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.field-label {
  @include typography-caption;
  @include font-weight-medium;
  color: #888;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.field-value {
  @include typography-small;
  @include font-weight-regular;
  color: #333;
}

.badge {
  @include typography-caption;
  @include font-weight-semibold;
  padding: 0.2rem 0.5rem;
  border-radius: $radius-sm;
  text-align: center;
  white-space: nowrap;
}

.badge-primary {
  background-color: #e67e22;
  color: white;
}

.badge-danger {
  background-color: #dc3545;
  color: white;
}

.card-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem 1rem 1rem 1rem;
  background: #fefefe;
  border-top: 1px solid var(--vt-c-white-soft);
}

.btn-primary-payment {
  width: 100%;
}

.card-actions-row {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 0.5rem;
}

.btn {
  @include typography-small;
  @include font-weight-medium;
  padding: 0.5rem 1rem;
  border-radius: $radius-sm;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #e67e22;
  border-color: #e67e22;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #d35400;
  border-color: #d35400;
}

.btn-secondary {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #5a6268;
  border-color: #545b62;
}

.btn-outline-primary {
  background-color: transparent;
  border-color: #e67e22;
  color: #e67e22;
}

.btn-outline-primary:hover:not(:disabled) {
  background-color: #e67e22;
  color: white;
}

.btn-outline-success {
  background-color: transparent;
  border-color: #28a745;
  color: #28a745;
}

.btn-outline-success:hover:not(:disabled) {
  background-color: #28a745;
  color: white;
}

.btn-outline-warning {
  background-color: transparent;
  border-color: #ffc107;
  color: #856404;
}

.btn-outline-warning:hover:not(:disabled) {
  background-color: #ffc107;
  color: #212529;
}

.btn-outline-danger {
  background-color: transparent;
  border-color: #dc3545;
  color: #dc3545;
}

.btn-outline-danger:hover:not(:disabled) {
  background-color: #dc3545;
  color: white;
}

.btn-danger {
  background-color: #dc3545;
  border-color: #dc3545;
  color: white;
}

.btn-danger:hover:not(:disabled) {
  background-color: #c82333;
  border-color: #bd2130;
}

.btn-sm {
  @include typography-caption;
  padding: 0.5rem 0.5rem;
  justify-content: center;
  text-align: center;
  white-space: nowrap;
}

.btn-sm i {
  @include typography-caption;
  margin-right: 0.25rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(black, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: $z-modal;
}

.modal-content {
  background: white;
  border-radius: $radius-md;
  box-shadow: 0 4px 20px rgba(black, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #dee2e6;
}

.modal-header h5 {
  margin: 0;
  color: #333;
}

.btn-close {
  @include typography-h4;
  background: none;
  border: none;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-close:hover {
  color: #333;
}

.modal-body {
  padding: 1.5rem;
}

.form-group {
  margin-bottom: 1.5rem;
}

.form-group label {
  @include font-weight-medium;
  display: block;
  margin-bottom: 0.5rem;
  color: #333;
}

.form-control {
  width: 100%;
  padding: 0.75rem;
  @include typography-body;
  border: 1px solid #ced4da;
  border-radius: $radius-sm;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.form-control:focus {
  outline: none;
  border-color: #e67e22;
  box-shadow: 0 0 0 0.2rem rgba(#e67e22, 0.25);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  @include typography-small;
  display: block;
  width: 100%;
  margin-top: 0.25rem;
  color: #dc3545;
}

.modal-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 1.5rem;
}

.card-preview {
  background-color: var(--vt-c-white-soft);
  padding: 1rem;
  border-radius: $radius-sm;
  margin-top: 1rem;
  font-family: monospace;
}

.notification {
  position: fixed;
  top: 20px;
  right: 20px;
  padding: 1rem 1.5rem;
  border-radius: $radius-md;
  box-shadow: 0 4px 12px rgba(black, 0.2);
  z-index: $z-notification;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  max-width: 400px;
  animation: slideIn 0.3s ease-out;
}

.notification.success {
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
  color: #155724;
}

.notification.error {
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
  color: #721c24;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

@media (max-width: 767px) {
  .ccma-wallet-information {
    padding: $spacing-2-5;
  }

  .cards-grid {
    grid-template-columns: 1fr;
  }

  .card-actions {
    flex-direction: column;
  }

  .card-actions .btn {
    width: 100%;
    justify-content: center;
  }

  .section-header {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }

  .modal-content {
    width: 95%;
    margin: 1rem;
  }

  .modal-actions {
    flex-direction: column;
  }

  .notification {
    right: 10px;
    left: 10px;
    max-width: none;
  }
}
</style>
